import { parseGID, checkPreventPurchase } from './utils';
import { checkVariantInStock } from '../utils/check-variant-in-stock';

class AjaxService {
  static name = 'ajax';

  /**
   * Gets a cart instance from a cart ID
   * @param {string} id cart id
   * @returns formatted cart object
   */

  async get() {
    return fetch(`${window.Shopify.routes.root}cart.js`)
      .then((response) => response.json());
  }

  async add(cart, payload) {
    const {preventPurchase, preventedItem} = 
      await checkPreventPurchase(payload, cart);
    if (preventPurchase) {
      const error = new Error("Prevent purchase with other items");
      error.data = { item: preventedItem };
      return Promise.reject(error);
    }

    const payloadArray = Array.isArray(payload) ? payload : [payload];
    const body = {
      items: payloadArray.map(({
        merchandiseId, quantity, attributes, id,
      }) => {
        const properties = (attributes || []).reduce(
          (acc, cur) => ({...acc, [cur.key]: cur.value}), {}
        );

        return {
          id: parseGID(merchandiseId) || parseGID(id),
          quantity,
          properties,
        }
      })
    }

    const { id } = body.items[0];
    const { items } = await this.get();
    let cartQuantity = 0;

    if(items.length) {
      const cartItem = items.find(item => item.id === Number(id));
      if(cartItem?.quantity) {
        cartQuantity = cartItem.quantity;
      }
    }
    const isVariantInStock = await checkVariantInStock(
      id, cartQuantity
    );

    if(isVariantInStock) {
      return fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      }).then(this.get);
    }

    return Promise.reject(
      new Error("Out of stock", { id: body.items[0].id })
    );
  }

  async remove(_, payload) {
    if (!payload) return null;
    const body = {
      updates: Object.fromEntries(payload.map((id) => [id, 0]))
    }
    
    return fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }).then(response => response.json());
  }

  async update(_, payload) {
    if (!payload) return null;
    let body;
    if (payload.note) body = { note: payload.note };
    else if (payload.attributes) body = { attributes: payload.attributes };
    else {
      body = {
        updates: Object.fromEntries(
          payload.map((item) => [
            item.id, item.quantity,
          ])
        )
      }
    }

    const { id, quantity, currentQty } = payload[0];
    const qty = quantity > currentQty ? currentQty : quantity
    const isVariantInStock = await checkVariantInStock(id, qty);

    if(isVariantInStock) {
      return fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      }).then(response => response.json());
    }

    return Promise.reject(
      new Error("Out of stock", { id })
    );
  }

  // AJAX Cart only supports updating properties on one line item at a time so payload only accepts an object
  async updateAttributes(_, payload) {
    if (!payload) return null;
    const { attributes, id, quantity } = payload;
    const body = {
      id,
    }
    if (quantity) body.quantity = quantity;
    if (attributes) body.properties = attributes;
    return fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }).then(response => response.json());
  }
}

const AjaxServiceSingleton = new AjaxService();
export default AjaxServiceSingleton;
